import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {PaiPlush} from '../mascot/PaiPlush';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {Hop, ToyShell, type MapToyProps} from './mapToys';
import {playTones} from './toySounds';

/**
 * Map-sized Stanford toys: campus landmarks and traditions that do something
 * delightful when clicked — Hoover Tower's carillon chimes, a dancing tree
 * busts a move, a bike rings its bell, Beanie goes fountain hopping, and the
 * Dish picks up a signal. Drawn generically in felt; no official marks and
 * no visible text (each toy's status is in its accessible name).
 *
 * Like the other map toys, each click replays its animation by remounting a
 * non-focusable inner group keyed by the click count.
 */

const SANDSTONE = '#EAD6AE';
const SANDSTONE_DARK = '#C9B48A';
const TERRACOTTA = '#C8553D';

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ── Hoover Tower ─────────────────────────────────────────────────────── */

/** The carillon's tune: the first phrase of the Westminster chimes. */
const CARILLON = [659.3, 523.3, 587.3, 392.0].map((frequency, index) => ({
  frequency,
  startSec: index * 0.45,
  durationSec: 1.3,
}));

const sway = keyframes`
  0%, 100% { transform: rotate(0); }
  25% { transform: rotate(-3deg); }
  60% { transform: rotate(2.5deg); }
`;

const floatUp = keyframes`
  0% { transform: translate(0, 0) scale(0.6); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translate(var(--drift), -34px) scale(1.1); opacity: 0; }
`;

const Sway = styled.g`
  transform-box: fill-box;
  transform-origin: center bottom;
  animation: ${sway} 1.6s ease-in-out;
  ${reducedMotion}
`;

const Note = styled.g<{$delaySec: number; $drift: number}>`
  --drift: ${({$drift}) => $drift}px;
  opacity: 0;
  animation: ${floatUp} 1.4s ease-out ${({$delaySec}) => $delaySec}s forwards;
  ${reducedMotion}
`;

/**
 * Hoover Tower: click and the carillon plays, the tower sways, and notes
 * drift out of the belfry.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyHooverTower({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [rings, setRings] = useState(0);

  const tower = (
    <>
      <rect
        x={13}
        y={104}
        width={34}
        height={12}
        rx={2}
        fill={SANDSTONE_DARK}
      />
      <rect
        x={19}
        y={40}
        width={22}
        height={66}
        fill={SANDSTONE}
        filter={`url(#${ids.fuzz})`}
      />
      {[24, 33].map(x => (
        <rect
          key={x}
          x={x}
          y={48}
          width={3}
          height={50}
          rx={1.5}
          fill={SANDSTONE_DARK}
        />
      ))}
      <rect x={15} y={37} width={30} height={4} rx={1} fill={SANDSTONE_DARK} />
      <rect x={17} y={24} width={26} height={14} fill={SANDSTONE} />
      {[20, 27.5, 35].map(x => (
        <path
          key={x}
          d={`M ${x} 36 V ${29} A 2.5 2.5 0 0 1 ${x + 5} 29 V 36 Z`}
          fill="#5A4632"
        />
      ))}
      <path
        d="M 16 25 Q 30 2 44 25 Z"
        fill={TERRACOTTA}
        filter={`url(#${ids.fuzz})`}
      />
      <path
        d="M 22 20 Q 27 12 31 9"
        stroke="#fff"
        strokeOpacity={0.35}
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
      />
      <rect x={28} y={3} width={4} height={6} fill={SANDSTONE} />
      <circle cx={30} cy={2} r={1.8} fill={TERRACOTTA} />
    </>
  );

  return (
    <ToyShell
      isPreview={isPreview}
      label="Hoover Tower"
      caption={
        rings === 0
          ? 'ring the carillon'
          : `rang ${rings} ${rings === 1 ? 'time' : 'times'}`
      }
      onPoke={() => {
        setRings(rings + 1);
        playTones({tones: CARILLON});
      }}
    >
      <svg
        viewBox="0 0 60 120"
        width={size}
        height={size * 2}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        {rings === 0 ? <g>{tower}</g> : <Sway key={rings}>{tower}</Sway>}
        {rings > 0 &&
          [
            {delaySec: 0, drift: -14, x: 22},
            {delaySec: 0.45, drift: 12, x: 36},
            {delaySec: 0.9, drift: -8, x: 26},
            {delaySec: 1.35, drift: 16, x: 34},
          ].map(({delaySec, drift, x}) => (
            <Note
              key={`${rings}-${delaySec}`}
              $delaySec={delaySec}
              $drift={drift}
            >
              {/* An eighth note: head, stem, flag. */}
              <g transform={`translate(${x} 30)`}>
                <ellipse
                  cx={0}
                  cy={0}
                  rx={2.6}
                  ry={1.9}
                  fill={FELT.cardinal}
                  transform="rotate(-20)"
                />
                <path
                  d="M 2.3 -0.6 V -9 Q 6.5 -7.5 5.5 -3.5"
                  stroke={FELT.cardinal}
                  strokeWidth={1.2}
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            </Note>
          ))}
      </svg>
    </ToyShell>
  );
}

/* ── Dancing tree ─────────────────────────────────────────────────────── */

const DANCES = [
  {
    name: 'the wiggle!',
    frames: keyframes`
      0%, 100% { transform: rotate(0); }
      15% { transform: rotate(-14deg); }
      30% { transform: rotate(12deg); }
      45% { transform: rotate(-10deg); }
      60% { transform: rotate(9deg); }
      80% { transform: rotate(-4deg); }
    `,
  },
  {
    name: 'the spin!',
    frames: keyframes`
      0%, 100% { transform: scaleX(1); }
      25% { transform: scaleX(-1); }
      50% { transform: scaleX(1); }
      75% { transform: scaleX(-1); }
    `,
  },
  {
    name: 'the hop!',
    frames: keyframes`
      0%, 100% { transform: translateY(0) scale(1, 1); }
      10%, 50% { transform: translateY(0) scale(1.1, 0.88); }
      25%, 65% { transform: translateY(-22%) scale(0.94, 1.08); }
      80% { transform: translateY(0) scale(1.04, 0.96); }
    `,
  },
  {
    name: 'the shimmy!',
    frames: keyframes`
      0%, 100% { transform: translateX(0) skewX(0); }
      12% { transform: translateX(-8%) skewX(8deg); }
      25% { transform: translateX(8%) skewX(-8deg); }
      37% { transform: translateX(-8%) skewX(8deg); }
      50% { transform: translateX(8%) skewX(-8deg); }
      75% { transform: translateX(-3%) skewX(3deg); }
    `,
  },
] as const;

const fall = keyframes`
  0% { transform: translate(0, 0) rotate(0); opacity: 1; }
  100% { transform: translate(var(--drift), 46px) rotate(220deg); opacity: 0; }
`;

const Dancer = styled.g<{$dance: number}>`
  transform-box: fill-box;
  transform-origin: center bottom;
  animation: ${({$dance}) => (DANCES[$dance] ?? DANCES[0]).frames} 1.2s
    ease-in-out;
  ${reducedMotion}
`;

const Leaf = styled.ellipse<{$drift: number; $delaySec: number}>`
  --drift: ${({$drift}) => $drift}px;
  transform-box: fill-box;
  animation: ${fall} 1.3s ease-in ${({$delaySec}) => $delaySec}s forwards;
  ${reducedMotion}
`;

/**
 * A goofy felt tree with googly eyes: every click is a different dance move,
 * and it sheds a few leaves doing it.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyDancingTree({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [moves, setMoves] = useState(0);
  const dance = (moves - 1 + DANCES.length) % DANCES.length;
  // Googly pupils land somewhere new after every move.
  const pupil = {
    dx: [1.5, -2, 2, -1][moves % 4] ?? 0,
    dy: [2, 1, -1.5, 2.5][moves % 4] ?? 0,
  };

  const tree = (
    <>
      <ellipse cx={30} cy={95} rx={8} ry={4} fill={FELT.cardinal} />
      <ellipse cx={50} cy={95} rx={8} ry={4} fill={FELT.cardinal} />
      <rect x={35} y={76} width={10} height={17} rx={3} fill="#7E5638" />
      <path
        d="M 14 60 L 2 52 M 66 60 L 78 50"
        stroke="#7E5638"
        strokeWidth={4}
        strokeLinecap="round"
      />
      <g filter={`url(#${ids.fuzz})`}>
        <path d="M 40 4 L 58 30 L 22 30 Z" fill="#4FA85E" />
        <path d="M 40 16 L 64 50 L 16 50 Z" fill="#46A057" />
        <path d="M 40 30 L 70 80 L 10 80 Z" fill="#3E8E4E" />
      </g>
      {[32, 48].map(cx => (
        <g key={cx}>
          <circle
            cx={cx}
            cy={46}
            r={7.5}
            fill="#fff"
            stroke="#2E6B3A"
            strokeWidth={1}
          />
          <circle
            cx={cx + pupil.dx}
            cy={46 + pupil.dy}
            r={3.4}
            fill={FELT.ink}
            style={{transition: 'cx 0.2s, cy 0.2s'}}
          />
        </g>
      ))}
      <path d="M 31 60 Q 40 72 49 60 Z" fill={FELT.ink} />
      <ellipse cx={40} cy={65.5} rx={4.5} ry={2.4} fill="#E97C8A" />
    </>
  );

  return (
    <ToyShell
      isPreview={isPreview}
      label="Dancing tree"
      caption={moves === 0 ? 'make it dance' : (DANCES[dance]?.name ?? '')}
      onPoke={() => {
        setMoves(moves + 1);
      }}
    >
      <svg
        viewBox="0 0 80 100"
        width={size}
        height={size * 1.25}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        {moves === 0 ? (
          <g>{tree}</g>
        ) : (
          <Dancer key={moves} $dance={dance}>
            {tree}
          </Dancer>
        )}
        {moves > 0 &&
          [
            {cx: 22, cy: 40, drift: -16, delaySec: 0.1},
            {cx: 58, cy: 36, drift: 14, delaySec: 0.3},
            {cx: 30, cy: 62, drift: -10, delaySec: 0.5},
            {cx: 54, cy: 64, drift: 12, delaySec: 0.2},
            {cx: 42, cy: 22, drift: 6, delaySec: 0.6},
          ].map(({cx, cy, drift, delaySec}) => (
            <Leaf
              key={`${moves}-${cx}`}
              cx={cx}
              cy={cy}
              rx={3.2}
              ry={1.8}
              fill="#6CC47C"
              $drift={drift}
              $delaySec={delaySec}
            />
          ))}
      </svg>
    </ToyShell>
  );
}

/* ── Bike ─────────────────────────────────────────────────────────────── */

/** Two bright bell dings. */
const BELL = [0, 0.2].map(startSec => ({
  frequency: 1318.5,
  startSec,
  durationSec: 0.45,
}));

const scoot = keyframes`
  0%, 100% { transform: translateX(0); }
  40% { transform: translateX(12%); }
  70% { transform: translateX(-4%); }
`;

const spinWheel = keyframes`
  from { transform: rotate(0); }
  to { transform: rotate(720deg); }
`;

const ringBell = keyframes`
  0%, 100% { transform: rotate(0); }
  20%, 60% { transform: rotate(-25deg); }
  40%, 80% { transform: rotate(25deg); }
`;

const Scoot = styled.g`
  animation: ${scoot} 1.1s ease-in-out;
  ${reducedMotion}
`;

const Wheel = styled.g<{$isSpinning: boolean}>`
  transform-box: fill-box;
  transform-origin: center;
  ${({$isSpinning}) =>
    $isSpinning &&
    css`
      animation: ${spinWheel} 1.1s ease-in-out;
    `}
  ${reducedMotion}
`;

const BellGroup = styled.g<{$isRinging: boolean}>`
  transform-box: fill-box;
  transform-origin: center bottom;
  ${({$isRinging}) =>
    $isRinging &&
    css`
      animation: ${ringBell} 0.5s ease-in-out;
    `}
  ${reducedMotion}
`;

/** One bike wheel with spokes, centered at `(cx, cy)`. */
function BikeWheel({
  cx,
  cy,
  isSpinning,
}: {
  cx: number;
  cy: number;
  isSpinning: boolean;
}) {
  return (
    <Wheel $isSpinning={isSpinning}>
      <circle
        cx={cx}
        cy={cy}
        r={15}
        fill="none"
        stroke={FELT.ink}
        strokeWidth={3.2}
      />
      {[0, 45, 90, 135].map(angle => (
        <path
          key={angle}
          d={`M ${cx - 14 * Math.cos((angle * Math.PI) / 180)} ${cy - 14 * Math.sin((angle * Math.PI) / 180)} L ${cx + 14 * Math.cos((angle * Math.PI) / 180)} ${cy + 14 * Math.sin((angle * Math.PI) / 180)}`}
          stroke={FELT.ink}
          strokeWidth={0.8}
          opacity={0.5}
        />
      ))}
      <circle cx={cx} cy={cy} r={2.2} fill={FELT.ink} />
    </Wheel>
  );
}

/**
 * A campus bike with Beanie riding in the basket: click to ring the bell,
 * spin the wheels, and scoot.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyBike({size, isPreview = false}: MapToyProps) {
  const [rings, setRings] = useState(0);
  const isMoving = rings > 0;

  const bike = (
    <>
      <BikeWheel cx={24} cy={52} isSpinning={isMoving} />
      <BikeWheel cx={86} cy={52} isSpinning={isMoving} />
      <path
        d="M 24 52 L 48 52 L 68 28 L 40 28 Z M 48 52 L 37 21 M 68 28 L 86 52 M 68 28 L 71 18"
        stroke={FELT.cardinal}
        strokeWidth={3.6}
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M 31 19 H 43"
        stroke={FELT.ink}
        strokeWidth={4}
        strokeLinecap="round"
      />
      <path
        d="M 66 17 H 76"
        stroke={FELT.ink}
        strokeWidth={2.6}
        strokeLinecap="round"
      />
      {/* Basket with a tiny Beanie. */}
      <circle cx={84} cy={14} r={7} fill={FELT.cream} />
      <path d="M 77.5 12 Q 84 1 90.5 12 Z" fill={FELT.cardinal} />
      <circle cx={84} cy={3.5} r={2} fill={FELT.cardinal} />
      <circle cx={81.6} cy={15} r={0.9} fill={FELT.ink} />
      <circle cx={86.4} cy={15} r={0.9} fill={FELT.ink} />
      <path d="M 76 18 H 93 L 90 30 H 79 Z" fill="#C9A77C" />
      <path d="M 78 22 H 91 M 79 26 H 90" stroke="#A6835A" strokeWidth={1} />
      <BellGroup key={rings} $isRinging={isMoving}>
        <circle cx={72} cy={14} r={3} fill={FELT.gold} />
      </BellGroup>
    </>
  );

  return (
    <ToyShell
      isPreview={isPreview}
      label="Bike"
      caption={rings === 0 ? 'ring the bell' : `ring ring! ×${rings}`}
      onPoke={() => {
        setRings(rings + 1);
        playTones({tones: BELL, wave: 'triangle'});
      }}
    >
      <svg
        viewBox="0 0 110 70"
        width={size}
        height={size * (70 / 110)}
        overflow="visible"
        aria-hidden
      >
        {rings === 0 ? <g>{bike}</g> : <Scoot key={rings}>{bike}</Scoot>}
      </svg>
    </ToyShell>
  );
}

/* ── Fountain hopping ─────────────────────────────────────────────────── */

/** A cheerful "bloop" for the splash. */
const SPLASH = [
  {frequency: 520, startSec: 0.55, durationSec: 0.18},
  {frequency: 330, startSec: 0.66, durationSec: 0.3},
];

const jet = keyframes`
  0% { transform: scaleY(0.1); opacity: 0.2; }
  30% { transform: scaleY(1.15); opacity: 1; }
  100% { transform: scaleY(0.2); opacity: 0; }
`;

const cannonball = keyframes`
  0% { transform: translate(-190%, -120%) rotate(-40deg); opacity: 1; }
  45% { transform: translate(-110%, -220%) rotate(80deg); opacity: 1; }
  75% { transform: translate(-50%, -20%) rotate(200deg) scale(0.95); opacity: 1; }
  100% { transform: translate(-50%, 30%) rotate(360deg) scale(0.8); opacity: 0; }
`;

const ripple = keyframes`
  0% { transform: scale(0.2); opacity: 0.9; }
  100% { transform: scale(1.6); opacity: 0; }
`;

const Jet = styled.path<{$delaySec: number}>`
  transform-box: fill-box;
  transform-origin: center bottom;
  opacity: 0;
  animation: ${jet} 1.3s ease-out ${({$delaySec}) => $delaySec}s;
  ${reducedMotion}
`;

const Ripple = styled.ellipse`
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  animation: ${ripple} 0.9s ease-out 0.6s;
  ${reducedMotion}
`;

const Hopper = styled.div`
  position: absolute;
  left: 50%;
  bottom: 18%;
  pointer-events: none;
  opacity: 0;
  animation: ${cannonball} 0.95s ease-in forwards;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const FountainStage = styled.div`
  position: relative;
`;

/**
 * A campus fountain: click and the jets whoosh up while Beanie cannonballs
 * in — fountain hopping.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyFountain({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [hops, setHops] = useState(0);

  return (
    <ToyShell
      isPreview={isPreview}
      label="Fountain hopping"
      caption={
        hops === 0 ? 'go fountain hopping' : `fountain hopping! 💦 ×${hops}`
      }
      onPoke={() => {
        setHops(hops + 1);
        playTones({tones: SPLASH, wave: 'triangle'});
      }}
    >
      <FountainStage>
        <svg
          viewBox="0 0 100 80"
          width={size}
          height={size * 0.8}
          overflow="visible"
          aria-hidden
        >
          <defs>
            <FeltDefs ids={ids} />
          </defs>
          <ellipse
            cx={50}
            cy={58}
            rx={46}
            ry={15}
            fill="#D8CFC0"
            filter={`url(#${ids.fuzz})`}
          />
          <ellipse cx={50} cy={57} rx={39} ry={10} fill="#7CC4E8" />
          <ellipse
            cx={42}
            cy={55}
            rx={14}
            ry={2.5}
            fill="#fff"
            opacity={0.35}
          />
          <rect x={46} y={36} width={8} height={20} rx={2} fill="#CFC5B4" />
          <ellipse cx={50} cy={36} rx={13} ry={3.5} fill="#D8CFC0" />
          <path
            d="M 50 34 Q 50 24 50 22"
            stroke="#9FD8F2"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
          {hops > 0 && (
            <g key={hops}>
              {[
                {d: 'M 50 34 Q 34 -10 18 48', delaySec: 0},
                {d: 'M 50 34 Q 50 -22 50 34', delaySec: 0.1},
                {d: 'M 50 34 Q 66 -10 82 48', delaySec: 0.05},
              ].map(({d, delaySec}) => (
                <Jet
                  key={d}
                  d={d}
                  stroke="#9FD8F2"
                  strokeWidth={3}
                  strokeLinecap="round"
                  fill="none"
                  $delaySec={delaySec}
                />
              ))}
              <Ripple
                cx={50}
                cy={57}
                rx={16}
                ry={4}
                fill="none"
                stroke="#fff"
                strokeWidth={2}
              />
            </g>
          )}
          {/* Front lip of the basin, drawn over the water. */}
          <path d="M 4 58 Q 50 84 96 58 Q 50 76 4 58 Z" fill="#CFC5B4" />
        </svg>
        {hops > 0 && (
          <Hopper key={hops}>
            <PaiPlush size={size * 0.32} state="happy" />
          </Hopper>
        )}
      </FountainStage>
    </ToyShell>
  );
}

/* ── The Dish ─────────────────────────────────────────────────────────── */

/** What the Dish hears, in order. */
const SIGNALS = [
  'hello 👋',
  'π = 3.14159…',
  'go Card! 🌲',
  'P(aliens) = ?',
  '·–·· ·· –·–',
  'send more data',
] as const;

/** Three rising beeps. */
const BEEPS = [880, 1174.7, 1568].map((frequency, index) => ({
  frequency,
  startSec: index * 0.12,
  durationSec: 0.1,
}));

const pulse = keyframes`
  0% { transform: scale(0.3); opacity: 0.9; }
  100% { transform: scale(1.5); opacity: 0; }
`;

const Aim = styled.g`
  transition: transform 0.7s cubic-bezier(0.34, 1.3, 0.64, 1);
`;

const Wave = styled.path<{$delaySec: number}>`
  transform-box: fill-box;
  transform-origin: center bottom;
  opacity: 0;
  animation: ${pulse} 1s ease-out ${({$delaySec}) => $delaySec}s 2;
  ${reducedMotion}
`;

/**
 * The Dish, the radio telescope in the foothills: click to swivel it, send
 * out waves, and receive a message.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyDish({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [pings, setPings] = useState(0);
  const angle = pings === 0 ? -18 : ([-32, 14, -6, 26, -24, 6][pings % 6] ?? 0);

  return (
    <ToyShell
      isPreview={isPreview}
      label="The Dish"
      caption={
        pings === 0
          ? 'listen to the sky'
          : `signal: ${SIGNALS[(pings - 1) % SIGNALS.length]}`
      }
      onPoke={() => {
        setPings(pings + 1);
        playTones({tones: BEEPS, wave: 'triangle'});
      }}
    >
      <svg
        viewBox="0 0 100 90"
        width={size}
        height={size * 0.9}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <path
          d="M -4 90 Q 30 58 60 66 Q 84 70 104 90 Z"
          fill="#D8C27A"
          filter={`url(#${ids.fuzz})`}
        />
        <path
          d="M 10 90 Q 36 70 58 74"
          stroke="#C4AD62"
          strokeWidth={2}
          fill="none"
        />
        <path
          d="M 50 54 L 40 72 M 50 54 L 60 72 M 50 54 V 72"
          stroke="#8E96A3"
          strokeWidth={2.4}
          strokeLinecap="round"
        />
        <Aim
          style={{
            transform: `rotate(${angle}deg)`,
            transformOrigin: '50px 50px',
          }}
        >
          <path
            d="M 24 34 Q 50 66 76 34 Z"
            fill="#F4F2EC"
            stroke="#C9CED6"
            strokeWidth={2}
          />
          <path
            d="M 30 38 Q 50 58 70 38"
            stroke="#fff"
            strokeWidth={1.5}
            fill="none"
            opacity={0.8}
          />
          <path
            d="M 34 38 L 50 22 L 66 38"
            stroke="#8E96A3"
            strokeWidth={1.4}
            fill="none"
          />
          <circle cx={50} cy={22} r={2.8} fill={FELT.cardinal} />
          {pings > 0 && (
            <g key={pings}>
              {[0, 0.25, 0.5].map(delaySec => (
                <Wave
                  key={delaySec}
                  d="M 38 14 Q 50 4 62 14"
                  stroke={FELT.gold}
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  fill="none"
                  $delaySec={delaySec}
                />
              ))}
            </g>
          )}
        </Aim>
        <circle cx={50} cy={52} r={3} fill="#8E96A3" />
        {pings > 0 && (
          <Hop key={`star-${pings}`}>
            <path
              d="M 84 4 L 86 9 L 91 10 L 87 13 L 88 18 L 84 15.5 L 80 18 L 81 13 L 77 10 L 82 9 Z"
              fill={FELT.gold}
            />
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}
